// DocumentUpload component with drag-and-drop, image compression, progress bar, and file preview
// PDFs are uploaded as-is (never re-serialized) and gated only by the size limit.

import { useState, useCallback, useRef, useEffect } from 'react'
import { logger } from '@/utils/logger'

interface DocumentUploadProps {
  value: File | null
  onChange: (file: File | null) => void
  accept?: string
  maxSizeMB?: number
  label?: string
  required?: boolean
  id?: string
}

// Format file size for display
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// Compress image file — always resizes to max 1920px and converts to JPEG
async function compressImage(
  file: File,
  maxSizeMB: number,
  onProgress?: (progress: number) => void
): Promise<File | null> {
  // Only compress images
  if (!file.type.startsWith('image/')) return file

  const maxBytes = maxSizeMB * 1024 * 1024

  onProgress?.(10)

  return new Promise((resolve) => {
    const img = new Image()
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    img.onload = () => {
      URL.revokeObjectURL(img.src)
      onProgress?.(30)

      // Guard against null canvas context (GPU issues, too many active canvases)
      if (!ctx) {
        logger.warn({ area: 'document-upload' }, `Canvas 2D context unavailable for ${file.name} — using original file`)
        onProgress?.(100)
        resolve(file)
        return
      }

      // Calculate new dimensions (max 1920px on longest side)
      let { width, height } = img
      const maxDimension = 1920

      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = (height / width) * maxDimension
          width = maxDimension
        } else {
          width = (width / height) * maxDimension
          height = maxDimension
        }
      }

      canvas.width = width
      canvas.height = height
      ctx.drawImage(img, 0, 0, width, height)

      onProgress?.(60)

      // Fix filename extension — all image types become .jpg after compression
      const outputName = file.name.replace(/\.(png|gif|webp|heic|heif|bmp|tiff?)$/i, '.jpg')

      // Iterative quality stepping: try 0.8 → 0.6 → 0.4
      const qualities = [0.8, 0.6, 0.4]
      let qualityIndex = 0

      const tryCompress = () => {
        const quality = qualities[qualityIndex]
        canvas.toBlob(
          (blob) => {
            if (blob && blob.size <= maxBytes) {
              onProgress?.(90)
              const compressedFile = new File([blob], outputName, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              })
              onProgress?.(100)
              resolve(compressedFile)
            } else if (qualityIndex < qualities.length - 1) {
              // Try next lower quality
              qualityIndex++
              tryCompress()
            } else if (blob) {
              // Last quality still too large — return it anyway, processFile will check size
              onProgress?.(90)
              const compressedFile = new File([blob], outputName, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              })
              onProgress?.(100)
              resolve(compressedFile)
            } else {
              logger.warn({ area: 'document-upload' }, `canvas.toBlob() returned null for ${file.name} (${file.type}) — using original file`)
              onProgress?.(100)
              resolve(file)
            }
          },
          'image/jpeg',
          quality
        )
      }

      tryCompress()
    }

    img.onerror = () => {
      URL.revokeObjectURL(img.src)
      if (file.type === 'image/heic' || file.type === 'image/heif') {
        // Browser can't process HEIC — signal failure so caller can show a helpful message
        onProgress?.(100)
        resolve(null)
      } else {
        logger.warn({ area: 'document-upload' }, `Image load failed for ${file.name} (${file.type}) during compression — using original file`)
        onProgress?.(100)
        resolve(file)
      }
    }
    img.src = URL.createObjectURL(file)
  })
}

// Generate preview URL for file
function generatePreviewUrl(file: File): string | null {
  if (file.type.startsWith('image/')) {
    return URL.createObjectURL(file)
  }
  // For PDFs, return null (we'll show a PDF icon instead)
  return null
}

export function DocumentUpload({
  value,
  onChange,
  accept = 'image/jpeg,image/png,image/gif,image/heic,image/heif,image/webp,.pdf',
  maxSizeMB = 5,
  label,
  required = false,
  id = 'document',
}: DocumentUploadProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Generate preview when value changes
  useEffect(() => {
    if (value) {
      const url = generatePreviewUrl(value)
      setPreviewUrl(url)
      // Cleanup function to revoke the URL
      return () => {
        if (url) {
          URL.revokeObjectURL(url)
        }
      }
    } else {
      setPreviewUrl(null)
    }
  }, [value])

  const processFile = useCallback(async (file: File) => {
    setError(null)
    setIsProcessing(true)
    setProgress(0)

    try {
      // Check file type
      const isImage = file.type.startsWith('image/')
      const isPDF = file.type === 'application/pdf'

      if (!isImage && !isPDF) {
        setError('Unsupported file type. Please upload a JPEG, PNG, GIF, HEIC, WebP, or PDF file.')
        return
      }

      // Images are compressed to fit the size limit. PDFs are NEVER rewritten:
      // re-serializing a PDF (e.g. via pdf-lib) corrupts encrypted/signed/XFA
      // documents — they upload "successfully" but won't open downstream. We
      // upload PDF bytes untouched and rely on the size limit below to reject
      // anything too large.
      let processedFile: File | null = file
      if (isImage) {
        processedFile = await compressImage(file, maxSizeMB, setProgress)
        if (!processedFile) {
          setError('HEIC/HEIF files are not supported on this browser. Please convert to JPEG or PNG and try again.')
          return
        }
      }

      // Check final size
      const maxBytes = maxSizeMB * 1024 * 1024
      if (processedFile.size > maxBytes) {
        const sizeMB = (processedFile.size / (1024 * 1024)).toFixed(1)
        if (isPDF) {
          setError(`This PDF is too large (${sizeMB} MB). PDFs with scanned images are often very large \u2014 try re-scanning at a lower resolution, or use fewer pages. Maximum file size is ${maxSizeMB} MB.`)
        } else if (processedFile !== file) {
          setError(`This image is still too large (${sizeMB} MB) after compression. Please take a new photo at lower resolution, or use a smaller image file.`)
        } else {
          setError(`This file is too large (${sizeMB} MB). Maximum file size is ${maxSizeMB} MB.`)
        }
        return
      }

      onChange(processedFile)
    } finally {
      setIsProcessing(false)
      setProgress(0)
    }
  }, [maxSizeMB, onChange])

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      processFile(file)
    }
  }, [processFile])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const file = e.dataTransfer.files?.[0]
    if (file) {
      processFile(file)
    }
  }, [processFile])

  const handleRemove = useCallback(() => {
    onChange(null)
    setError(null)
    setPreviewUrl(null)
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }, [onChange])

  const handleClick = useCallback(() => {
    inputRef.current?.click()
  }, [])

  const isPDF = value?.type === 'application/pdf'

  return (
    <div className="document-upload-wrapper">
      {label && (
        <label className="form-label">
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}

      <div
        className={`upload-zone ${isDragging ? 'upload-zone--dragging' : ''} ${value ? 'upload-zone--has-file' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={inputRef}
          type="file"
          id={id}
          accept={accept}
          onChange={handleFileChange}
          className="upload-zone__input"
          required={required && !value}
        />

        {isProcessing ? (
          <div className="upload-processing">
            <div className="upload-progress">
              <div className="upload-progress__bar" style={{ width: `${progress}%` }} />
            </div>
            <span className="upload-progress__text">Compressing... {progress}%</span>
          </div>
        ) : value ? (
          <div className="upload-preview">
            {/* File preview */}
            <div className="upload-preview__thumbnail">
              {previewUrl ? (
                <img src={previewUrl} alt="Preview" className="upload-preview__image" />
              ) : isPDF ? (
                <div className="upload-preview__pdf-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#E53935"/>
                    <path d="M14 2V8H20" fill="#FFCDD2"/>
                    <text x="12" y="17" textAnchor="middle" fontSize="6" fill="white" fontWeight="bold">PDF</text>
                  </svg>
                </div>
              ) : null}
            </div>
            <div className="upload-preview__info">
              <span className="upload-preview__name">{value.name}</span>
              <span className="upload-preview__size">{formatFileSize(value.size)}</span>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                handleRemove()
              }}
              className="upload-preview__remove"
            >
              Remove
            </button>
          </div>
        ) : (
          <div className="upload-zone__placeholder">
            <img src="/img/icon/icon_upload.svg" alt="" />
            <span>Click or drag to upload</span>
          </div>
        )}
      </div>

      {error && <div className="form-error">{error}</div>}
    </div>
  )
}
